import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Option } from 'react-select';
import classnames from 'classnames';

import {
  CATEGORY_SEARCH_NO_RESULTS_IDLE_MS,
  CategorySelectionPath,
  isOtherCategoryName,
  normalizeCategorySearchTerm,
} from '@utils/categorySearchTerm';

import './style.scss';

export interface CategoryPickerSelection {
  category: ICRMCategoryResponse;
  selectionPath: CategorySelectionPath;
  position: number;
  searchTerm: string;
  nameMatchCount: number;
  suggestedCount: number;
}

interface DropdownProps {
  options: CategoryOptionType[];
  suggestedOptions: CategoryOptionType[];
  handleCategorySelect: (e: ICRMCategoryResponse) => (e?: React.SyntheticEvent<any>) => void;
  getSuggestedCategories: (crmToken: string, description: string) => Promise<any>;
  crmToken: string;
  error: string;
  selectedCategory?: { id?: number; name?: string; label?: string } | null;
  onCategorySelected?: (selection: CategoryPickerSelection) => void;
  onCategorySearchNoResults?: (searchTerm: string) => void;
}

const compareCategoryNames = (left: { name: string }, right: { name: string }): number => {
  const leftName = left.name.trim().toLowerCase();
  const rightName = right.name.trim().toLowerCase();
  if (leftName < rightName) {
    return -1;
  }
  if (leftName > rightName) {
    return 1;
  }

  return 0;
};

/** Name matches for a search, with Other appended when the agency has it. */
const matchCategoriesForSearch = (source: CategoryOptionType[] | undefined, term: string): CategoryOptionType[] => {
  const needle = term.toLowerCase();
  const filtered = (source ?? []).filter((option) => option?.label?.toLowerCase()?.includes(needle));
  if (!filtered.some((option) => isOtherCategoryName(option?.name))) {
    const other = source?.find((option) => isOtherCategoryName(option?.name));
    if (other) {
      filtered.push(other);
    }
  }
  filtered.sort(compareCategoryNames);

  return filtered;
};

const nothingToPick = (nameMatches: CategoryOptionType[], suggestions: CategoryOptionType[]): boolean =>
  suggestions.length === 0 && nameMatches.every((option) => isOtherCategoryName(option?.name));

const MultiSectionDropdown: React.FC<DropdownProps> = ({
  options,
  handleCategorySelect,
  suggestedOptions,
  getSuggestedCategories,
  crmToken,
  error,
  selectedCategory,
  onCategorySelected,
  onCategorySearchNoResults,
}) => {
  const filteredOptionsArray = useMemo(
    () =>
      options?.map((item) => ({
        ...item,
        name: item?.name?.trim(),
        label: item?.label?.trim(),
      })),
    [options]
  );

  useEffect(() => {
    filteredOptionsArray?.sort(compareCategoryNames);
  }, [filteredOptionsArray]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);

  useEffect(() => {
    if (selectedCategory === undefined) {
      return;
    }

    if (!selectedCategory) {
      setSelectedOption(null);

      return;
    }

    setSelectedOption({
      ...selectedCategory,
      label: selectedCategory.label || selectedCategory.name,
    } as Option);
  }, [selectedCategory]);
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };
  const menuRef = useRef(null);
  const idPrefixRef = useRef(`multi-section-dropdown-${Math.random().toString(36).slice(2, 10)}`);
  const labelId = `${idPrefixRef.current}-label`;
  const valueId = `${idPrefixRef.current}-value`;
  const listboxId = `${idPrefixRef.current}-listbox`;
  const errorId = `${idPrefixRef.current}-error`;
  const suggestedLabelId = `${idPrefixRef.current}-suggested-label`;
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filteredOptions, setFilteredOptions] = useState<CategoryOptionType[]>(filteredOptionsArray);
  const [filteredSuggestedOptions, setFilteredSuggestedOptions] = useState<CategoryOptionType[]>(suggestedOptions);
  const searchTermRef = useRef(searchTerm);
  const optionsRef = useRef(options);
  const getSuggestedCategoriesRef = useRef(getSuggestedCategories);
  const crmTokenRef = useRef(crmToken);
  const onCategorySearchNoResultsRef = useRef(onCategorySearchNoResults);
  const suggestionRequestIdRef = useRef(0);
  const reportedNoResultTermsRef = useRef(new Set<string>());
  const noResultTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastKeystrokeAtRef = useRef(0);

  searchTermRef.current = searchTerm;
  optionsRef.current = options;
  getSuggestedCategoriesRef.current = getSuggestedCategories;
  crmTokenRef.current = crmToken;
  onCategorySearchNoResultsRef.current = onCategorySearchNoResults;

  const clearNoResultTimer = () => {
    if (noResultTimerRef.current != null) {
      clearTimeout(noResultTimerRef.current);
      noResultTimerRef.current = null;
    }
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      if (menuRef.current?.contains(event.target as Node)) {
        return;
      }
      setIsOpen(false);
    };

    document.addEventListener('click', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, []);
  useEffect(() => {
    lastKeystrokeAtRef.current = Date.now();
    clearNoResultTimer();
    setFilteredSuggestedOptions([]);
    if (searchTerm.trim() === '') {
      setFilteredOptions([]);
    }

    return () => clearNoResultTimer();
  }, [searchTerm]);
  useEffect(() => {
    if (searchTerm.trim()) {
      setFilteredOptions(matchCategoriesForSearch(options, searchTerm));
    }
  }, [searchTerm, options]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      return undefined;
    }

    const requestId = ++suggestionRequestIdRef.current;
    const termAtRequest = searchTerm;
    let active = true;

    getSuggestedCategoriesRef
      .current(crmTokenRef.current, termAtRequest)
      .then((res) => {
        if (!active || requestId !== suggestionRequestIdRef.current) {
          return;
        }

        const categoryIds: number[] = res?.response?.categoryIds ?? [];
        const suggested: CategoryOptionType[] = [];
        optionsRef.current?.forEach((opt) => {
          if (categoryIds.includes(opt?.id)) {
            opt.color = 'grey';
            suggested.push(opt);
          }
        });
        suggested.sort(compareCategoryNames);
        setFilteredSuggestedOptions(suggested);

        const normalized = normalizeCategorySearchTerm(termAtRequest);
        const nameMatches = matchCategoriesForSearch(optionsRef.current, termAtRequest);
        if (!normalized || reportedNoResultTermsRef.current.has(normalized) || !nothingToPick(nameMatches, suggested)) {
          return;
        }

        const elapsed = Date.now() - lastKeystrokeAtRef.current;
        const wait = Math.max(0, CATEGORY_SEARCH_NO_RESULTS_IDLE_MS - elapsed);
        noResultTimerRef.current = setTimeout(() => {
          noResultTimerRef.current = null;
          if (normalizeCategorySearchTerm(searchTermRef.current) !== normalized) {
            return;
          }
          if (reportedNoResultTermsRef.current.has(normalized)) {
            return;
          }
          const latestMatches = matchCategoriesForSearch(optionsRef.current, searchTermRef.current);
          if (!nothingToPick(latestMatches, suggested)) {
            return;
          }
          reportedNoResultTermsRef.current.add(normalized);
          onCategorySearchNoResultsRef.current?.(normalized);
        }, wait);
      })
      .catch(() => {
        // A failed suggestion request is not an empty result.
      });

    return () => {
      active = false;
    };
  }, [searchTerm]);

  const handleOptionClick = (option: CategoryOptionType, selectionPath: CategorySelectionPath, position: number) => {
    setSelectedOption(option);
    const { label, color, ...newObject } = option;
    const category: ICRMCategoryResponse = newObject;
    const isBrowsing = selectionPath === 'browse';
    onCategorySelected?.({
      category,
      selectionPath,
      position,
      searchTerm,
      nameMatchCount: isBrowsing ? 0 : filteredOptions?.length ?? 0,
      suggestedCount: isBrowsing ? 0 : filteredSuggestedOptions?.length ?? 0,
    });
    handleCategorySelect(category)(null);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleCloseOnEscape = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      setIsOpen(false);
    }
  };

  return (
    <div ref={menuRef} className={classnames('dropdown-wrapper', `${isOpen ? 'open' : 'hidden'}`)}>
      <div className="dropdown">
        <span id={labelId} className="h2-dropdown">
          Category
        </span>
        <button
          type="button"
          className="dropdown-btn"
          onClick={toggleDropdown}
          onKeyDown={handleCloseOnEscape}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-labelledby={`${labelId} ${valueId}`}
          aria-invalid={!!error}
          aria-describedby={error ? errorId : undefined}
        >
          <span id={valueId} className="dropdown__label">
            {selectedOption !== null ? selectedOption.label : 'Select One'}
          </span>
          <span className="dropdown__arrow_container" aria-hidden="true">
            <span className="dropdown__arrow"></span>
          </span>
        </button>
        {isOpen && (
          <div className="dropdown-content">
            <div className="dropdown-content__search-container">
              <span className="dropdown-content__search-icon" aria-hidden="true" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleCloseOnEscape}
                className="dropdown-content__search-input"
                aria-label="Search categories"
              />
            </div>
            <div className="dropdown-content__listbox" role="listbox" id={listboxId} aria-labelledby={labelId}>
              {searchTerm.trim() === '' &&
                filteredOptionsArray?.map((option, index) => (
                  <button
                    type="button"
                    key={index}
                    className="option-black"
                    role="option"
                    aria-selected={selectedOption?.id === option.id}
                    onClick={() => handleOptionClick(option, 'browse', index + 1)}
                    onKeyDown={handleCloseOnEscape}
                  >
                    {option?.label}
                  </button>
                ))}
              {filteredOptions?.map((option, index) => (
                <button
                  type="button"
                  key={index}
                  className="option-black"
                  role="option"
                  aria-selected={selectedOption?.id === option.id}
                  onClick={() => handleOptionClick(option, 'search_results', index + 1)}
                  onKeyDown={handleCloseOnEscape}
                >
                  {option?.label}
                </button>
              ))}
              {filteredSuggestedOptions?.length ? (
                <div className="dropdown-content__suggested-group" role="group" aria-labelledby={suggestedLabelId}>
                  <h3 id={suggestedLabelId} className="h3-suggested-category">
                    Suggested Categories
                  </h3>
                  {filteredSuggestedOptions?.map((option, index) => (
                    <button
                      type="button"
                      key={index}
                      className="option-grey"
                      role="option"
                      aria-selected={selectedOption?.id === option.id}
                      onClick={() => handleOptionClick(option, 'suggested', index + 1)}
                      onKeyDown={handleCloseOnEscape}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>
      {!!error && (
        <p id={errorId} className="error-h2" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};
export default MultiSectionDropdown;
